import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../context/GameContext";
import { useSeason } from "../context/SeasonContext";
import { supabase } from "../supabaseClient";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getMostRecentSunday = () => {
  const d = new Date();
  const day = d.getDay(); // 0 = Sunday
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

const calcPlayerPts = (s) =>
  (Number(s.three_pm) || 0) * 3 +
  (Number(s.two_pm) || 0) * 2 +
  (Number(s.ft_made) || 0);

const calcTeamScore = (playerStats) =>
  Object.values(playerStats).reduce((sum, s) => sum + calcPlayerPts(s), 0);

const emptyStats = () => ({
  three_pm: 0,
  two_pm: 0,
  ft_made: 0,
  rebounds: 0,
  assists: 0,
  personal_fouls: 0,
  technical_fouls: 0,
});

// ─── StatSheetPhoto ────────────────────────────────────────────────────────────
const StatSheetPhoto = ({ photoUrl, onSetPhoto, onClear }) => {
  const [zoomed, setZoomed] = useState(false);

  const handleFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      onSetPhoto(URL.createObjectURL(file));
    }
  };

  if (!photoUrl) {
    return (
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 text-center">
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
          📷 Paste (Ctrl/Cmd+V) or upload the stat sheet photo to reference
          while entering
        </p>
        <label className="inline-block text-sm font-bold text-orange-600 dark:text-orange-400 cursor-pointer">
          Choose photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2 shrink-0">
        <button onClick={() => setZoomed(true)} className="shrink-0">
          <img
            src={photoUrl}
            alt="Stat sheet"
            className="w-10 h-10 rounded-lg object-cover border dark:border-gray-700"
          />
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400 flex-1">
          Stat sheet attached · tap to view
        </span>
        <button
          onClick={onClear}
          className="text-xs font-bold text-red-500 shrink-0"
        >
          Remove
        </button>
      </div>

      {zoomed && (
        <div
          className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4"
          onClick={() => setZoomed(false)}
        >
          <img
            src={photoUrl}
            alt="Stat sheet full view"
            className="max-w-full max-h-full rounded-lg"
          />
          <button
            onClick={() => setZoomed(false)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 text-white font-bold flex items-center justify-center"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};

// ─── StatsGridTable ────────────────────────────────────────────────────────────
const STAT_FIELDS = [
  { key: "three_pm", label: "3PM" },
  { key: "two_pm", label: "2PM" },
  { key: "ft_made", label: "FTM" },
  { key: "rebounds", label: "REB" },
  { key: "assists", label: "AST" },
  { key: "personal_fouls", label: "PF" },
  { key: "technical_fouls", label: "TF" },
];

const StatsGridTable = ({ players, playerStats, onChange }) => {
  const cellRefs = React.useRef({});

  const focusCell = (playerIdx, colIdx) => {
    const ref = cellRefs.current[`${playerIdx}-${colIdx}`];
    if (ref) {
      ref.focus();
      ref.select();
    }
  };

  const handleKeyDown = (e, playerIdx, colIdx) => {
    const lastRow = players.length - 1;
    const lastCol = STAT_FIELDS.length - 1;
    switch (e.key) {
      case "ArrowDown":
      case "Enter":
        e.preventDefault();
        if (playerIdx < lastRow) focusCell(playerIdx + 1, colIdx);
        else if (colIdx < lastCol) focusCell(0, colIdx + 1); // wrap to top of next stat column
        break;
      case "ArrowUp":
        e.preventDefault();
        if (playerIdx > 0) focusCell(playerIdx - 1, colIdx);
        else if (colIdx > 0) focusCell(lastRow, colIdx - 1);
        break;
      case "ArrowRight":
        e.preventDefault();
        if (colIdx < lastCol) focusCell(playerIdx, colIdx + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (colIdx > 0) focusCell(playerIdx, colIdx - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-700">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800">
            <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 min-w-[140px]">
              Player
            </th>
            {STAT_FIELDS.map((f) => (
              <th
                key={f.key}
                className="px-1 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 min-w-[48px]"
              >
                {f.label}
              </th>
            ))}
            <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 min-w-[48px]">
              PTS
            </th>
          </tr>
        </thead>
        <tbody>
          {players.map((player, playerIdx) => {
            const stats = playerStats[player.id] ?? emptyStats();
            const pts = calcPlayerPts(stats);
            return (
              <tr
                key={player.id}
                className="border-t border-gray-100 dark:border-gray-700"
              >
                <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 px-3 py-2 whitespace-nowrap">
                  <span className="text-orange-500 dark:text-orange-400 font-black tabular-nums mr-1.5">
                    #{player.jersey_number ?? "–"}
                  </span>
                  <span className="font-semibold text-gray-800 dark:text-gray-100">
                    {player.name}
                  </span>
                </td>
                {STAT_FIELDS.map((f, colIdx) => (
                  <td key={f.key} className="px-0.5 py-1 text-center">
                    <input
                      ref={(el) => {
                        cellRefs.current[`${playerIdx}-${colIdx}`] = el;
                      }}
                      type="number"
                      inputMode="numeric"
                      value={stats[f.key]}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => handleKeyDown(e, playerIdx, colIdx)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "") {
                          onChange(player.id, { ...stats, [f.key]: "" });
                          return;
                        }
                        const parsed = parseInt(val, 10);
                        if (!isNaN(parsed))
                          onChange(player.id, {
                            ...stats,
                            [f.key]: Math.max(0, parsed),
                          });
                      }}
                      className="w-11 text-center font-bold tabular-nums rounded-lg border border-gray-200 dark:border-gray-700
                                                bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-1.5
                                                focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none
                                                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </td>
                ))}
                <td className="px-2 py-2 text-center">
                  <span
                    className={`font-black tabular-nums ${pts > 0 ? "text-emerald-500" : "text-gray-300 dark:text-gray-600"}`}
                  >
                    {pts}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── PaperGameModal ───────────────────────────────────────────────────────────

const PaperGameModal = ({
  teams,
  activeSeason,
  adminId,
  onClose,
  onSaved,
  playoffGame = null,
}) => {
  const isPlayoff = !!playoffGame;
  const [step, setStep] = useState(isPlayoff ? 2 : 1); // skip setup for playoff
  const [team1Id, setTeam1Id] = useState(
    isPlayoff ? String(playoffGame.team1.id) : "",
  );
  const [team2Id, setTeam2Id] = useState(
    isPlayoff ? String(playoffGame.team2.id) : "",
  );
  const [gameDate, setGameDate] = useState(getMostRecentSunday());
  const [isScrimmage, setIsScrimmage] = useState(false);
  const [team1Players, setTeam1Players] = useState([]);
  const [team2Players, setTeam2Players] = useState([]);
  const [playerStats, setPlayerStats] = useState({});
  const [activeTeamTab, setActiveTeamTab] = useState("team1");
  const [saving, setSaving] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [statSheetPhoto, setStatSheetPhoto] = useState(null);

  const sortedTeams = [...teams].sort((a, b) => a.name.localeCompare(b.name));
  const team1 = teams.find((t) => String(t.id) === String(team1Id));
  const team2 = teams.find((t) => String(t.id) === String(team2Id));

  const team1Score = calcTeamScore(
    Object.fromEntries(
      team1Players.map((p) => [p.id, playerStats[p.id] ?? emptyStats()]),
    ),
  );
  const team2Score = calcTeamScore(
    Object.fromEntries(
      team2Players.map((p) => [p.id, playerStats[p.id] ?? emptyStats()]),
    ),
  );

  const loadPlayers = useCallback(async (t1, t2) => {
    if (!t1 && !t2) return;
    setLoadingPlayers(true);
    const ids = [t1, t2].filter(Boolean);
    const results = await Promise.all(
      ids.map((id) =>
        supabase
          .from("players")
          .select("id, name, jersey_number")
          .eq("team_id", id)
          .order("jersey_number", { ascending: true }),
      ),
    );
    const [r1, r2] =
      t1 && t2
        ? results
        : t1
          ? [results[0], { data: [] }]
          : [{ data: [] }, results[0]];
    const t1p = r1.data || [];
    const t2p = r2.data || [];
    setTeam1Players(t1p);
    setTeam2Players(t2p);
    // Seed stats for any new players
    setPlayerStats((prev) => {
      const next = { ...prev };
      [...t1p, ...t2p].forEach((p) => {
        if (!next[p.id]) next[p.id] = emptyStats();
      });
      return next;
    });
    setLoadingPlayers(false);
  }, []);

  const handleTeamChange = (side, val) => {
    if (side === 1) setTeam1Id(val);
    else setTeam2Id(val);
  };

  useEffect(() => {
    if (team1Id || team2Id) loadPlayers(team1Id, team2Id);
  }, [team1Id, team2Id, loadPlayers]);

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            setStatSheetPhoto((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return URL.createObjectURL(file);
            });
          }
          e.preventDefault();
          break;
        }
      }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  useEffect(
    () => () => {
      if (statSheetPhoto) URL.revokeObjectURL(statSheetPhoto);
    },
    [statSheetPhoto],
  );

  const updateStat = (playerId, stats) => {
    setPlayerStats((prev) => ({ ...prev, [playerId]: stats }));
  };

  const handleSave = async () => {
    if (isPlayoff) {
      if (!team1Id || !team2Id) return;
    } else {
      if (!team1Id || !team2Id || !gameDate) return;
    }
    setSaving(true);
    try {
      const allPlayers = [...team1Players, ...team2Players];
      const statsPayload = allPlayers
        .filter((p) => {
          const s = playerStats[p.id];
          return s && Object.values(s).some((v) => Number(v) !== 0);
        })
        .map((p) => ({ player_id: p.id, ...playerStats[p.id] }));

      const { error } = await supabase.functions.invoke("admin-actions", {
        body: isPlayoff
          ? {
              action: "recordPlayoffPaperResult",
              adminId,
              payload: {
                gameId: playoffGame.id,
                team1Score,
                team2Score,
                playerStats: statsPayload,
              },
            }
          : {
              action: "recordPaperGame",
              adminId,
              payload: {
                seasonId: activeSeason.id,
                team1_id: team1Id,
                team2_id: team2Id,
                team1Score,
                team2Score,
                game_date: `${gameDate}T20:05:00`,
                is_scrimmage: isScrimmage,
                playerStats: statsPayload,
              },
            },
      });
      if (error) throw error;
      onSaved();
    } catch (err) {
      alert(`Error saving game: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const canProceed = team1Id && team2Id && team1Id !== team2Id && gameDate;

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex flex-col overflow-hidden">
      <div className="bg-white dark:bg-gray-900 flex flex-col h-full max-h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-5 pb-3 border-b dark:border-gray-800 shrink-0">
          <div>
            <h2 className="text-xl font-black text-gray-900 dark:text-white">
              Record Paper Game
            </h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {step === 1
                ? "Step 1 of 2 — Game setup"
                : "Step 2 of 2 — Enter stats"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500
                            dark:text-gray-400 flex items-center justify-center font-bold text-lg
                            touch-manipulation active:scale-95"
          >
            ✕
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex px-4 py-2 gap-2 shrink-0">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full transition-colors ${s <= step ? "bg-orange-500" : "bg-gray-200 dark:bg-gray-700"}`}
            />
          ))}
        </div>

        {/* Step 1: Setup */}
        {step === 1 && (
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
            {/* Date */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Game Date
              </label>
              <input
                type="date"
                value={gameDate}
                onChange={(e) => setGameDate(e.target.value)}
                className="w-full border-2 dark:border-gray-700 rounded-xl px-4 py-3
                                    bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                    focus:border-orange-500 focus:outline-none text-base font-semibold"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Defaults to most recent Sunday
              </p>
            </div>

            {/* Game type */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Game Type
              </label>
              <div className="flex gap-3">
                {[
                  { val: false, label: "Regular", sub: "Counts for W/L" },
                  { val: true, label: "Scrimmage", sub: "No W/L impact" },
                ].map((opt) => (
                  <button
                    key={String(opt.val)}
                    onClick={() => setIsScrimmage(opt.val)}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 text-left transition-all touch-manipulation
                                            ${
                                              isScrimmage === opt.val
                                                ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                                            }`}
                  >
                    <div
                      className={`font-bold text-sm ${isScrimmage === opt.val ? "text-orange-600 dark:text-orange-400" : "text-gray-700 dark:text-gray-300"}`}
                    >
                      {opt.label}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      {opt.sub}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Teams */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Teams
              </label>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-1">
                    Team 1
                  </p>
                  <select
                    value={team1Id}
                    onChange={(e) => handleTeamChange(1, e.target.value)}
                    className="w-full border-2 dark:border-gray-700 rounded-xl px-4 py-3
                                            bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                            focus:border-orange-500 focus:outline-none font-semibold"
                  >
                    <option value="">Select team…</option>
                    {sortedTeams
                      .filter((t) => String(t.id) !== String(team2Id))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-1">
                    Team 2
                  </p>
                  <select
                    value={team2Id}
                    onChange={(e) => handleTeamChange(2, e.target.value)}
                    className="w-full border-2 dark:border-gray-700 rounded-xl px-4 py-3
                                            bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                            focus:border-orange-500 focus:outline-none font-semibold"
                  >
                    <option value="">Select team…</option>
                    {sortedTeams
                      .filter((t) => String(t.id) !== String(team1Id))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Score preview */}
            {team1 && team2 && (
              <div className="bg-gray-900 rounded-2xl px-5 py-4 flex items-center justify-between">
                <div className="text-center flex-1">
                  <p className="text-xs font-bold text-orange-400 uppercase truncate mb-0.5">
                    {team1.name}
                  </p>
                  <p className="text-4xl font-black text-orange-400 tabular-nums">
                    {team1Score}
                  </p>
                </div>
                <span className="text-gray-600 font-black text-xl">–</span>
                <div className="text-center flex-1">
                  <p className="text-xs font-bold text-sky-400 uppercase truncate mb-0.5">
                    {team2.name}
                  </p>
                  <p className="text-4xl font-black text-sky-400 tabular-nums">
                    {team2Score}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Stats */}
        {step === 2 && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Live score bar */}
            <div className="bg-gray-900 px-4 py-3 flex items-center justify-between shrink-0">
              <div className="text-center flex-1">
                <p className="text-xs font-bold text-orange-400 uppercase truncate mb-0.5">
                  {team1?.name}
                </p>
                <p className="text-3xl font-black text-orange-400 tabular-nums">
                  {team1Score}
                </p>
              </div>
              <span className="text-gray-600 font-black text-lg">–</span>
              <div className="text-center flex-1">
                <p className="text-xs font-bold text-sky-400 uppercase truncate mb-0.5">
                  {team2?.name}
                </p>
                <p className="text-3xl font-black text-sky-400 tabular-nums">
                  {team2Score}
                </p>
              </div>
            </div>

            {/* Team tabs */}
            <div className="flex border-b dark:border-gray-800 shrink-0">
              {[
                {
                  key: "team1",
                  label: team1?.name ?? "Team 1",
                  score: team1Score,
                  color: "text-orange-500",
                },
                {
                  key: "team2",
                  label: team2?.name ?? "Team 2",
                  score: team2Score,
                  color: "text-sky-500",
                },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTeamTab(tab.key)}
                  className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors touch-manipulation ${
                    activeTeamTab === tab.key
                      ? `border-orange-500 ${tab.color}`
                      : "border-transparent text-gray-400 dark:text-gray-500"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`ml-1.5 text-xs font-black tabular-nums ${activeTeamTab === tab.key ? tab.color : "text-gray-400"}`}
                  >
                    {tab.score}
                  </span>
                </button>
              ))}
            </div>

            {/* Stat sheet photo upload */}
            <div className="px-3 pt-2 shrink-0">
              <StatSheetPhoto
                photoUrl={statSheetPhoto}
                onSetPhoto={setStatSheetPhoto}
                onClear={() => {
                  if (statSheetPhoto) URL.revokeObjectURL(statSheetPhoto);
                  setStatSheetPhoto(null);
                }}
              />
            </div>

            {/* Player stat rows */}
            <div className="flex-1 overflow-y-auto px-3 py-3">
              {loadingPlayers ? (
                <p className="text-center text-gray-400 py-8">
                  Loading players…
                </p>
              ) : (activeTeamTab === "team1" ? team1Players : team2Players)
                  .length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">
                  No players on this roster.
                </p>
              ) : (
                <StatsGridTable
                  players={
                    activeTeamTab === "team1" ? team1Players : team2Players
                  }
                  playerStats={playerStats}
                  onChange={updateStat}
                />
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-3 border-t dark:border-gray-800 flex gap-3 shrink-0 bg-white dark:bg-gray-900">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 py-3 font-semibold text-gray-600 dark:text-gray-400
                                    border dark:border-gray-700 rounded-2xl touch-manipulation"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!canProceed}
                className="flex-1 py-3 font-bold text-white bg-orange-600 hover:bg-orange-700
                                    disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-400
                                    rounded-2xl touch-manipulation active:scale-95 transition-all"
              >
                Next — Enter Stats →
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 font-semibold text-gray-600 dark:text-gray-400
                                    border dark:border-gray-700 rounded-2xl touch-manipulation"
              >
                ← Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 font-bold text-white bg-emerald-600 hover:bg-emerald-700
                                    disabled:bg-emerald-400 rounded-2xl touch-manipulation active:scale-95 transition-all"
              >
                {saving ? "Saving…" : `Save Game (${team1Score}–${team2Score})`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ScorerSetup (root) ───────────────────────────────────────────────────────

const ScorerSetup = () => {
  const navigate = useNavigate();
  const { teams, startGame, resumeGame } = useGame();
  const { activeSeason } = useSeason();

  const [gameType, setGameType] = useState("regular");
  const isScrimmage = gameType === "scrimmage";

  const [Team1Id, setTeam1Id] = useState("");
  const [Team2Id, setTeam2Id] = useState("");
  const sortedTeams = [...(teams || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const team2Options = sortedTeams.filter(
    (t) => String(t.id) !== String(Team1Id),
  );

  const [todayGames, setTodayGames] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);
  const [strandedGames, setStrandedGames] = useState([]);
  const [showPaperModal, setShowPaperModal] = useState(false);
  const [adminId, setAdminId] = useState(null);

  const [paperPlayoffGame, setPaperPlayoffGame] = useState(null);

  useEffect(() => {
    const loadAdmin = async () => {
      const { data } = await supabase.auth.getUser();
      setAdminId(data?.user?.id || null);
    };
    loadAdmin();
  }, []);

  useEffect(() => {
    const fetchGames = async () => {
      if (!activeSeason) {
        setTodayLoading(false);
        return;
      }

      const now = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(now.getDate() - 7);

      // Helper to format Date objects to YYYY-MM-DD
      const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };

      const formattedToday = formatDate(now);
      const formattedSevenDaysAgo = formatDate(sevenDaysAgo);

      const isPlayoffs = activeSeason.status === "playoffs";

      let query = supabase
        .from("games")
        .select(
          `
                    id, game_date, court, game_type, playoff_round,
                    team1:teams!games_team1_id_fkey(id, name, logo_url),
                    team2:teams!games_team2_id_fkey(id, name, logo_url)
                `,
        )
        .eq("season_id", activeSeason.id)
        .eq("status", "scheduled");

      if (isPlayoffs) {
        query = query
          .eq("game_type", "playoff")
          .not("team1_id", "is", null)
          .not("team2_id", "is", null); // hide the empty QF/SF/Final slots
      } else {
        query = query
          .eq("game_type", "regular")
          .gte("game_date", `${formattedSevenDaysAgo}T00:00:00`)
          .lte("game_date", `${formattedToday}T23:59:59`);
      }

      query = query
        .order("game_date", { ascending: true })
        .order("court", { ascending: true });
      const { data: scheduled } = await query;

      setTodayGames(scheduled || []);

      const { data: live } = await supabase
        .from("live_games")
        .select(
          `
                    id, team1_id, team2_id, team1_score, team2_score, half, is_scrimmage, game_id, updated_at,
                    team1:teams!live_games_team1_id_fkey(id, name, logo_url, season_id),
                    team2:teams!live_games_team2_id_fkey(id, name, logo_url, season_id)
                `,
        )
        .eq("status", "live")
        .order("updated_at", { ascending: false });

      const filtered = (live || []).filter(
        (g) => g.team1?.season_id === activeSeason.id,
      );
      setStrandedGames(filtered);
      setTodayLoading(false);
    };

    fetchGames();
  }, [activeSeason]);

  const handleStartGame = async (
    t1,
    t2,
    scrimmage = false,
    existingGameId = null,
  ) => {
    await startGame(t1, t2, scrimmage, existingGameId);
    navigate("/scorer/live");
  };

  const handleResumeGame = async (liveGameRow) => {
    await resumeGame(liveGameRow);
    navigate("/scorer/live");
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold dark:text-white">Start New Game</h1>
        {activeSeason?.status !== "playoffs" && (
          <button
            onClick={() => setShowPaperModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold
                            border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300
                            rounded-xl hover:border-orange-400 hover:text-orange-600 dark:hover:text-orange-400
                            transition-all touch-manipulation active:scale-95"
          >
            📋 Paper Game
          </button>
        )}
      </div>

      {/* Paper game modal */}
      {showPaperModal && (
        <PaperGameModal
          teams={teams}
          activeSeason={activeSeason}
          adminId={adminId}
          onClose={() => setShowPaperModal(false)}
          onSaved={() => {
            setShowPaperModal(false);
            alert("Paper game recorded successfully!");
          }}
        />
      )}

      {paperPlayoffGame && (
        <PaperGameModal
          playoffGame={paperPlayoffGame}
          teams={teams}
          activeSeason={activeSeason}
          adminId={adminId}
          onClose={() => setPaperPlayoffGame(null)}
          onSaved={() => {
            setPaperPlayoffGame(null);
            alert("Playoff result recorded!");
          }}
        />
      )}

      {/* ── Stranded / resumable games ── */}
      {strandedGames.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">⚠️</span>
            <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
              Resume In-Progress Game
            </h2>
          </div>
          <div className="space-y-3">
            {strandedGames.map((game) => (
              <div
                key={game.id}
                className="flex items-center justify-between bg-red-50 dark:bg-red-900/20
                                    border-2 border-red-300 dark:border-red-700 rounded-xl px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {game.team1.logo_url ? (
                      <img
                        src={game.team1.logo_url}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <span className="shrink-0">🏀</span>
                    )}
                    <span className="font-bold text-gray-900 dark:text-white truncate">
                      {game.team1.name}
                    </span>
                  </div>
                  <span className="font-black tabular-nums text-red-600 dark:text-red-400 shrink-0">
                    {game.team1_score} – {game.team2_score}
                  </span>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {game.team2.logo_url ? (
                      <img
                        src={game.team2.logo_url}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <span className="shrink-0">🏀</span>
                    )}
                    <span className="font-bold text-gray-900 dark:text-white truncate">
                      {game.team2.name}
                    </span>
                  </div>
                  <div className="hidden sm:flex flex-col ml-2 shrink-0">
                    <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                      Half {game.half} · Interrupted
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      Last saved{" "}
                      {new Date(game.updated_at).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleResumeGame(game)}
                  className="ml-4 shrink-0 bg-red-600 hover:bg-red-700 active:scale-95
                                        text-white font-bold px-4 py-2 rounded-lg text-sm touch-manipulation transition-all"
                >
                  ▶ Resume
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Today's scheduled matchups ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">📅</span>
          <h2 className="text-xl font-bold dark:text-white">
            {activeSeason?.status === "playoffs"
              ? "Playoff Games"
              : "This Week's Games"}
          </h2>
        </div>

        {todayLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
            Checking the schedule…
          </p>
        ) : todayGames.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-4 py-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {activeSeason?.status === "playoffs"
                ? "No playoff games ready to score yet."
                : "No regular season games pending scores from the past week."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {todayGames.map((game) => (
              <div
                key={game.id}
                className="flex items-center justify-between bg-orange-50 dark:bg-orange-900/20
                                    border-2 border-orange-200 dark:border-orange-700 rounded-xl px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {game.team1.logo_url ? (
                      <img
                        src={game.team1.logo_url}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <span className="shrink-0">🏀</span>
                    )}
                    <span className="font-bold text-gray-900 dark:text-white truncate">
                      {game.team1.name}
                    </span>
                  </div>
                  <span className="text-gray-400 dark:text-gray-500 font-bold shrink-0">
                    vs
                  </span>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {game.team2.logo_url ? (
                      <img
                        src={game.team2.logo_url}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <span className="shrink-0">🏀</span>
                    )}
                    <span className="font-bold text-gray-900 dark:text-white truncate">
                      {game.team2.name}
                    </span>
                  </div>
                  <div className="hidden sm:flex flex-col ml-2 shrink-0">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(game.game_date).toLocaleTimeString("en-US", {
                        year: "2-digit",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                    {game.court && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {game.court}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  <button
                    onClick={() =>
                      handleStartGame(
                        game.team1.id,
                        game.team2.id,
                        false,
                        game.id,
                      )
                    }
                    className="bg-orange-600 hover:bg-orange-700 active:scale-95 text-white font-bold px-4 py-2 rounded-lg text-sm touch-manipulation transition-all"
                  >
                    ▶ Start
                  </button>
                  {activeSeason?.status === "playoffs" && (
                    <button
                      onClick={() => setPaperPlayoffGame(game)}
                      className="border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-orange-400 hover:text-orange-600 dark:hover:text-orange-400 font-bold px-3 py-2 rounded-lg text-sm touch-manipulation transition-all"
                    >
                      📋 Paper
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Manual setup ── */}
      {activeSeason?.status !== "playoffs" && (
        <div>
          <h2 className="text-xl font-bold mb-4 dark:text-white">
            Manual Setup
          </h2>

          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="font-semibold mb-2 dark:text-white">Game Type</h3>
            <div className="flex gap-4">
              {["regular", "scrimmage"].map((type) => (
                <label key={type} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="gameType"
                    value={type}
                    checked={gameType === type}
                    onChange={() => setGameType(type)}
                    className="mr-2"
                  />
                  <span className="dark:text-white capitalize">
                    {type === "regular"
                      ? "Regular Game"
                      : "Scrimmage (doesn't count)"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <p className="mb-6 text-gray-600 dark:text-gray-400 text-sm">
            Select two teams to begin live scoring.
          </p>

          {teams.length < 2 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏀</div>
              <p className="text-gray-600 dark:text-gray-400">
                Not enough teams registered yet. Need at least 2 teams to start
                a game.
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-xl p-6 shadow-sm">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-gray-300">
                    Team 1
                  </label>
                  <select
                    value={Team1Id}
                    onChange={(e) => {
                      setTeam1Id(e.target.value);
                      if (e.target.value === Team2Id) setTeam2Id("");
                    }}
                    className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
                  >
                    <option value="">Select Team 1</option>
                    {sortedTeams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-gray-300">
                    Team 2
                  </label>
                  <select
                    value={Team2Id}
                    onChange={(e) => setTeam2Id(e.target.value)}
                    disabled={!Team1Id}
                    className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none disabled:opacity-50"
                  >
                    <option value="">Select Team 2</option>
                    {team2Options.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                onClick={() => handleStartGame(Team1Id, Team2Id, isScrimmage)}
                disabled={!Team1Id || !Team2Id}
                className="w-full bg-orange-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-orange-700 transition shadow-lg shadow-orange-600/20 disabled:bg-gray-400 disabled:shadow-none disabled:cursor-not-allowed"
              >
                Start {isScrimmage ? "Scrimmage" : "Regular Game"}
              </button>
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Tip:</strong> Use "Today's Games" above for scheduled
              matchups. Use Manual Setup for scrimmages or unscheduled games.
              Use <strong>📋 Paper Game</strong> to record a game scored on
              paper.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScorerSetup;
